import { Check, PackagePlus, X } from 'lucide-react';
import { formatInTimeZone } from 'date-fns-tz';
import { cn } from '@/lib/cn';
import { TIMEZONE } from '@/lib/format';
import {
  buildStaffOrderTimelineRows,
  staffReachedInLabel,
  type StaffOrderTimelineInput,
  type StaffTimelineRow,
} from '@/lib/staffOrderTimeline';

const TZ = TIMEZONE;

function stampLines(stamp: string | null): { time: string; date: string } | null {
  if (!stamp) return null;
  return {
    time: formatInTimeZone(stamp, TZ, 'hh:mm aa'),
    date: formatInTimeZone(stamp, TZ, 'MMM d, yyyy'),
  };
}

function RowIcon({ row }: { row: StaffTimelineRow }) {
  if (row.variant === 'cancelled') {
    return (
      <span className="flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full bg-danger text-danger-foreground">
        <X className="h-3 w-3" aria-hidden />
      </span>
    );
  }
  if (row.variant === 'done') {
    return (
      <span className="flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full bg-success text-success-foreground">
        <Check className="h-3 w-3 stroke-[3]" aria-hidden />
      </span>
    );
  }
  return (
    <span className="flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full bg-muted-foreground/35 text-muted-foreground">
      <PackagePlus className="h-3 w-3 opacity-70" aria-hidden />
    </span>
  );
}

export function StaffOrderTimeline({
  order,
  compact,
  className,
  showHeader = true,
}: {
  order: StaffOrderTimelineInput;
  compact?: boolean;
  className?: string;
  showHeader?: boolean;
}) {
  const rows = buildStaffOrderTimelineRows(order);
  const reachedIn = staffReachedInLabel(order);

  return (
    <div className={cn('rounded-md border border-border bg-card', className)}>
      {showHeader ? (
        <div
          className={cn(
            'flex items-center justify-between gap-2 border-b border-border px-3 py-2',
            compact && 'py-1.5',
          )}
        >
          <div className="flex items-center gap-2">
            <span
              className={cn(
                'inline-flex h-6 w-6 items-center justify-center rounded border border-border bg-muted/50 text-muted-foreground',
                compact && 'h-5 w-5 text-[10px]',
              )}
              aria-hidden
            >
              +
            </span>
            <span className={cn('font-semibold text-foreground', compact ? 'text-xs' : 'text-sm')}>
              Timeline
            </span>
          </div>
          {reachedIn ? (
            <span className={cn('shrink-0 text-foreground', compact ? 'text-[10px]' : 'text-xs')}>
              {reachedIn}
            </span>
          ) : null}
        </div>
      ) : null}
      <div className={cn('relative', compact ? 'px-2 py-2' : 'px-3 py-3')}>
        <div
          className="pointer-events-none absolute bottom-4 left-[22px] top-4 w-px border-l border-dashed border-muted-foreground/45"
          aria-hidden
        />
        <ol className="relative m-0 list-none space-y-0 p-0">
          {rows.map((row) => (
            <li
              key={row.id}
              className={cn(
                'relative flex gap-3 border-b border-border/70 py-2.5 pl-1 last:border-b-0 last:pb-0 first:pt-0',
                compact && 'gap-2 py-2',
              )}
            >
              <div className="relative z-[1] flex w-6 shrink-0 justify-center pt-0.5">
                <RowIcon row={row} />
              </div>
              <div className="min-w-0 flex-1">
                <div
                  className={cn(
                    'font-medium leading-snug text-foreground',
                    compact ? 'text-[11px]' : 'text-sm',
                  )}
                >
                  {row.title}
                </div>
                <div
                  className={cn(
                    'mt-0.5 leading-snug text-muted-foreground',
                    compact ? 'text-[10px]' : 'text-xs',
                  )}
                >
                  {row.message}
                </div>
              </div>
              <div className="shrink-0 text-right">
                {(() => {
                  const lines = stampLines(row.stamp);
                  if (!lines) {
                    return (
                      <span
                        className={cn('text-muted-foreground', compact ? 'text-[10px]' : 'text-xs')}
                      >
                        —
                      </span>
                    );
                  }
                  return (
                    <div
                      className={cn(
                        'tabular-nums text-muted-foreground',
                        compact ? 'text-[10px]' : 'text-xs',
                      )}
                    >
                      <div className="font-medium text-foreground/80">{lines.time}</div>
                      <div>{lines.date}</div>
                    </div>
                  );
                })()}
              </div>
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}
